import { LicensingAgencyDocumentType } from '@/@types/generated'
import { LicensingAgencyStatus } from '@/@types/generated/models/LicensingAgencyStatus'
import { hasRole } from '@/components/Atoms/Permissions'
import { StepIndicator } from '@/components/Molecules/Stepper'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { helpEmail } from '@/data/texas'
import { useAuth } from '@/hooks/useAuth'
import format from '@/lib/format'
import { InternalRoles } from '@/lib/utils'
import { addBusinessDays, parseISO } from 'date-fns'
import { ClockIcon, FileTextIcon } from 'lucide-react'
import { useLicensingAgency } from '../hooks/licensing-agency-provider'
import { LicensingAgencyUpdateStatusDialog } from './dialogs/LicensingAgencyUpdateStatusDialog'
import { LicensingAgencyDocumentPreview } from './LicensingAgencyDocumentPreview'

const REVIEW_TURNAROUND_BUSINESS_DAYS = 3
const ACTIVE_STEP_INDEX = 1

export function LicensingAgencyUnderReviewAlert() {
  // Hooks
  const { user } = useAuth()
  const { licensingAgency, isSaving, approve, reject } = useLicensingAgency()

  // Not under review, nothing to show
  if (licensingAgency?.status !== LicensingAgencyStatus.UNDER_REVIEW)
    return null

  const canApprove = hasRole(InternalRoles, user)

  return (
    <div className="border-warning bg-warning-background flex flex-col gap-4 rounded-xl border p-6">
      <div className="flex items-start gap-4">
        <ClockIcon className="stroke-warning-foreground size-14" />

        <div className="flex flex-col gap-1">
          <span className="text-warning-foreground text-lg font-bold">
            {canApprove
              ? 'Application Waiting for Review'
              : 'Your Application Is Under Review'}
          </span>
          <span className="text-warning-foreground">
            {canApprove ? (
              <>
                Verify the EIN confirmation letter and applicant details, then
                update the status once a decision is made.
              </>
            ) : (
              <>
                We&apos;re reviewing the information you submitted. This
                typically takes {REVIEW_TURNAROUND_BUSINESS_DAYS - 2}-
                {REVIEW_TURNAROUND_BUSINESS_DAYS} business days — we&apos;ll
                email{' '}
                <span className="font-semibold">
                  {licensingAgency.emailAddress}
                </span>{' '}
                as soon as a decision is made. No action is needed from you
                right now.
              </>
            )}
          </span>
        </div>
      </div>

      <div className="bg-background rounded-xl border p-4">
        <StepIndicator
          activeIndex={ACTIVE_STEP_INDEX}
          steps={[
            {
              title: 'Submitted',
              description: format.Date(
                // non-null assertion safe; if agency is under review, certification *must* exist
                licensingAgency.certification!.certified.timestamp
              ),
            },
            { title: 'In Review', description: 'Current step' },
            {
              title: 'Decision',
              description: `Est. ${format.Date(
                addBusinessDays(
                  // non-null assertion safe; if agency is under review, certification *must* exist
                  parseISO(licensingAgency.certification!.certified.timestamp),
                  REVIEW_TURNAROUND_BUSINESS_DAYS
                ).toISOString()
              )}`,
            },
          ]}
        />
      </div>

      {canApprove ? (
        <div className="flex flex-wrap gap-3">
          <LicensingAgencyDocumentPreview
            agencyId={licensingAgency.id}
            documentType={LicensingAgencyDocumentType.EIN_CONFIRMATION_LETTER}
            title={`Review ${format.Enum(
              LicensingAgencyDocumentType,
              LicensingAgencyDocumentType.EIN_CONFIRMATION_LETTER
            )}`}
          >
            <Button variant="outline">
              <FileTextIcon />
              EIN Confirmation Letter
            </Button>
          </LicensingAgencyDocumentPreview>

          <LicensingAgencyUpdateStatusDialog
            status={licensingAgency?.status}
            statusOptions={[
              LicensingAgencyStatus.ACTIVE,
              LicensingAgencyStatus.REJECTED,
            ]}
            onSubmit={(status) =>
              status === LicensingAgencyStatus.ACTIVE ? approve() : reject()
            }
          >
            <Button disabled={isSaving}>
              {isSaving && <Spinner />}
              Update Status
            </Button>
          </LicensingAgencyUpdateStatusDialog>
        </div>
      ) : (
        <span>
          Questions about your application?{' '}
          <a
            href={`mailto:${helpEmail}`}
            className="text-primary font-semibold hover:underline"
          >
            Contact the licensing team
          </a>
        </span>
      )}
    </div>
  )
}
